<template>
    <widget-wrapper
    v-if="!headerCards"
    hideRedirectIcon
    :variant="variant"
    :title="t('home.widgets.fastLinks.title')">
        <div class="fast-links-wrapper" :class="[variant, {'mobile': getMobileAppState}]">
            <div v-for="item in parsedList" class="fast-link" :key="`fastLink${item.id}`" @click="fastLinkClicked(item)">
                <component :is="svgComponent(item.iconName)" class="icon"/>
                <span>{{item.description}}</span>
                <div v-if="item.isNew" class="new-tag">{{t('home.widgets.fastLinks.newTag')}}</div>
            </div>
        </div>
    </widget-wrapper>
    <div v-else class="header-cards-wrapper" :class="getAppThemeMode">
        <!-- Slider -->
        <slider-generic v-if="parsedList.length" :sliderList="parsedList" :class="{ 'center-cards': parsedList.length < 4 }">
            <div
            v-for="fastLink in parsedList"
            class="fast-links-card"
            :key="`accoundCard${fastLink.id}`"
            :style="{'width': headerCardWidth}"
            @click="fastLinkClicked(fastLink)">
                <div class="icon"><component :is="svgComponent(fastLink.iconName)"/></div>
                <div class="text">{{fastLink.description}}</div>
            </div>
        </slider-generic>
    </div>
</template>

<script setup>
import WidgetWrapper from '@/components/features/home/WidgetWrapper'
import SliderGeneric from '@/components/generic/sliders/Generic'
/* Vue & Assets */
import { ref, computed, defineAsyncComponent, onMounted } from 'vue'
import { navigateByPath, navigateByName } from '@/router/helpers/navigation'
import { getMenuItemData } from '@/assets/scripts/parsers/menu'
import { menuIconMapper } from '@/assets/scripts/mappers/menu'
import { routeList } from '@/router/helpers/routes'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Props
@headerCards {Boolean} - prop to style as cards for header
@variant {String} - can be [ none (default), anchor]
*/
const props = defineProps({
    headerCards: Boolean,
    selectedAccount: Number,
    variant: String
})

/* Data */
const showLoader = ref(props.showAccountsLoader)
const applyErrorState = ref(props.applyAccountsErrorState)
const headerCardWidth = ref('76px')

/* mapActions & mapGetters */
const { fetchDashboardCards } = mapActions(moduleMapper.HOME)
const { getDashboardCards } = mapGetters(moduleMapper.HOME)
const { getMobileAppState, getAppThemeMode } = mapGetters()

const fetchFastLinks = async () => {
    showLoader.value = true
    applyErrorState.value = false

	try {
        await fetchDashboardCards()
        showLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		showLoader.value = false
        applyErrorState.value = true
	}
}

const parsedList = computed(() => {
    /*
    * it's possible that we receive top5 entries that are disabled by backoffice
    * since we can't show those entries we need to also filter top5
    */
    return getDashboardCards.value.reduce((acc, card) => {
        if (card.menuItemView && getMenuItemData(card.menuItemView.id)) {
            const menuData = getMenuItemData(card.menuItemView.id)
            acc.push({
                isAppMenuEntry: true,
                isNew: card.isNew,
                description: card.description,
                link: menuData.link,
                iconName: card.iconId || menuIconMapper(menuData.label.split('_')[0]),
                additionalData: menuData.additionalData,
                subEntryId: card.subEntryId,
                dashboardCardAdditionalData: card.dashboardCardAdditionalData
            })
        } else if (!card.menuItemView && card.url) {
            acc.push({
                isNew: card.isNew,
                description: card.description,
                subDescription: card.subDescription,
                link: card.url,
                iconName: card.iconId || 'consult'
            })
        }
        return acc
    }, [])
})

const svgComponent = iconName => {
    return defineAsyncComponent(() => import('@/assets/icons/' + iconName + '.svg?inline'))
}

const fastLinkClicked = item => {
    if (item.subEntryId || item.dashboardCardAdditionalData || item.isAppMenuEntry) {
        const routePathKey = Object.keys(routeList).find(key => routeList[key] === item.link)
        const routeName = routePathKey.replace('_PATH', '_NAME')
        if (item.subEntryId || item.dashboardCardAdditionalData) {
            /* For charge Cards we need to have this logic because we don't want it to first fo to "my cards" feature */
            if (item?.subEntryId === 'charge_kamba') {
                navigateByName(routeList.CARDS_CHARGE_DIRECT_KAMBA_NAME)
                return
            } else if (item?.subEntryId === 'charge_flex') {
                navigateByName(routeList.CARDS_CHARGE_FLEX_NAME)
                return
            } else if (item?.subEntryId === 'charge_virtual') {
                navigateByName(routeList.CARDS_CHARGE_VIRTUAL_NAME)
                return
            } else if (item?.subEntryId === 'charge_mcx') {
                navigateByName(routeList.CARDS_CHARGE_MULTICAIXA_NAME)
                return
            } navigateByName(routeList[routeName], { dashboardCardInfo: { ...item, accountId: props.selectedAccount } })
        } else navigateByName(routeList[routeName], { accountId: props.selectedAccount })
    } else navigateByPath(item.link)
}

fetchFastLinks()

onMounted(() => {
    headerCardWidth.value = `${(window.innerWidth - 56) / 4}px`
})
</script>

<style lang="scss" scoped>
.fast-links-wrapper {
    width: 100%;
    height: auto;
    padding: 16px 24px 0 24px;

    display: flex;
    flex-direction: column;
    gap: 8px;

    .fast-link {
        width: 100%;
        height: 56px;
        padding: 0 16px;

        display: flex;
        align-items: center;
        gap: 8px;

        @include H5-Medium;
        color: var(--secondary-dark-3);

        background-color: var(--secondary-light-4);
        border-radius: 8px;
        cursor: pointer;

        svg {
            width: 16px;
            height: 16px;
            margin-right: 8px;

            :deep(.change-color-fill) {
                fill: var(--secondary-dark-4);
            }
        }

        .new-tag {
            width: fit-content;
            height: 16px;
            padding: 0 4px;

            @include H9-Regular;
            color: var(--secondary-light-4);
            line-height: 16px;

            background-color: var(--negative);
            border-radius: 4px;
        }
    }

    &:not(.anchor) {
        .fast-link {
            background-color: var(--secondary-light-3);
        }
    }

    &.mobile {
        padding: 16px 16px 0 16px;
    }
}

.header-cards-wrapper {
    width: 100%;
    height: 100px;
    margin-top: -10px;
    margin-bottom: 20px;

    :deep(.slider-content-wrapper) {
        .slider-wrapper {
            padding-left: 16px;
        }

        .circles-wrapper {
            visibility: hidden;
        }

        &.center-cards {
            .slider-wrapper {
                justify-content: center;
                padding-left: 32px;
            }
        }
    }

    .fast-links-card {
        min-width: 76px;
        height: 80px;
        padding: 8px 4px;

        display: flex;
        flex-shrink: 0;
        flex-direction: column;
        align-items: center;
        gap: 6px;

        background-color: rgba(var(--secondary-light-4-rgb), 0.2);
        border-radius: 15px;

        .icon svg {
            width: 24px;
            height: 24px;

            flex-shrink: 0;

            :deep(.change-color-fill) {
                fill: var(--secondary-light-4);
            }
        }

        .text {
            height: 32px;

            display: flex;
            align-items: center;

            @include H7-Regular;
            color: var(--secondary-light-4);
            text-align: center;
            line-height: 16px;
        }

        &:active {
            background-color: rgba(var(--secondary-light-4-rgb), 0.4);
        }
    }

    &.dark {
        .fast-links-card {
            background-color: rgba(var(--secondary-dark-4-rgb), 0.2);

            .icon svg {
                :deep(.change-color-fill) {
                    fill: var(--secondary-dark-4);
                }
            }

            .text {
                color: var(--secondary-dark-4);
            }

            &:active {
                background-color: rgba(var(--secondary-dark-4-rgb), 0.4);
            }
        }
    }
}

</style>
